<template>
	<div
		class="form"
		role="form"
		:aria-label="name"
	>
		<!--
			Displays a message that can either be a default one provided
			in the translations files or through props.

			Also list errors if they are provided through the errors props.
		-->
		<div
			v-if="hasMessage"
			ref="message"
			class="form__message"
			data-cy="form-message"
		>
			<helio-message
				:active="true"
				:title="message.title"
				:state="message.state"
				@close="removeMessage"
			>
				<template v-slot:content>
					{{ message.text }}

					<ul v-if="state === 'failure' && flattenedErrors.length > 0">
						<li
							v-for="(error, errorIndex) in flattenedErrors"
							:key="errorIndex"
						>
							<span v-html="error" />
						</li>
					</ul>
				</template>
			</helio-message>
		</div>

		<!--
			Displays a custom header if provided through a slot named "header".
		-->
		<div
			v-if="hasHeaderSlot"
			class="form__header"
		>
			<slot name="header" />
		</div>

		<!--
			Displays a custom content if provided through a slot named "content".

			The "size" props is given back to the slot in order to give the
			possibility to the custom content to use it on the various form
			component it contains.

			The "savable" prop is given back to the slot in order to  allow the
			possibility to use a custom submit button, other than the one from the
			helio-form footer

			Since it will be use on the application side with the slot="content"
			and slot-scope="slotProps" syntax, we must look in the $scopedSlots
			variables in order to determine if it exist instead of the usual
			$slots variables.
		-->
		<div
			v-if="$scopedSlots.content"
			class="form__content"
		>
			<slot
				name="content"
				:size="size"
				:savable="savable"
			/>
		</div>

		<!--
			Displays a custom footer if provided through a slot named "footer".

			The hideFooter props is a boolean that can hide the default footer.
		-->
		<div
			v-if="!hideFooter"
			class="form__footer"
		>
			<!--
				Multiple props are given back to the slot in order to give them the
				possibility to use them in the custom footer in the application.

				Since it will be use on the application side with the slot="footer"
				and slot-scope="slotProps" syntax, we must look in the $scopedSlots
				variables in order to determine if it exist instead of the usual
				$slots variables.
			-->
			<template v-if="$scopedSlots.footer">
				<slot
					name="footer"
					:savable="savable"
					:modified="modified"
					:size="size"
					:submit-button-state="submitButtonState"
					:translation="$t('form')"
				/>
			</template>

			<!--
				If no footer scoped slots or hideFooter props are provided,
				we use the default footer.
			-->
			<template v-else>
				<helio-button-group
					alignment="left"
					:segmented="false"
				>
					<helio-button
						:state="submitButtonState"
						:size="size.button"
						:text="$t('form.save')"
						:disabled="!savable"
						color="primary"
						@click="submit"
					/>

					<helio-button
						v-if="modified"
						:size="size.button"
						:text="$t('form.cancel')"
						@click="reset"
					/>
				</helio-button-group>
			</template>
		</div>
	</div>
</template>

<script>
import Button from '../button/Button.vue';
import ButtonGroup from '../button-group/ButtonGroup.vue';
import Message from '../message/Message.vue';
import messageMixin from './mixins/message';
import stateMixin from './mixins/state';
import sizeMixin from './mixins/size';

export default {
	name: 'Form',

	components: {
		'helio-button': Button,
		'helio-button-group': ButtonGroup,
		'helio-message': Message,
	},

	/*
		In order to avoid to ended up with a bloated component,
		we've separated the responsabilities of the component
		in different mixins.
	*/
	mixins: [
		messageMixin,
		stateMixin,
		sizeMixin,
	],

	props: {
		data: {
			type: Object,
			required: false,
			default: null,
			description: 'Supplies information about its state, errors and fields to the Form component',
		},

		hideFooter: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the footer will be hidden',
		},

		name: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the aria-label of the Form component',
		},
	},

	slots: {
		content: {
			required: false,
			scoped: true,
			description: 'Supplies content to the main section of the Form',
		},

		footer: {
			required: false,
			scoped: true,
			description: 'Supplies content to the Form\'s footer',
		},

		header: {
			required: false,
			scoped: false,
			description: 'Supplies content to the Form\'s header',
		},
	},

	computed: {
		hasHeaderSlot() {
			return !!this.$slots.header || !!this.$scopedSlots.header;
		},
	},

	methods: {
		submit() {
			this.removeMessage();
			this.$emit('submit', this.data);
		},
	},

};
</script>

<style lang="scss" scoped src="./form.scss" />
